import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Text, Badge, makeStyles, tokens } from '@fluentui/react-components';
import { Issue } from '../types';

interface IssueListProps {
  issues: Issue[];
  currentPath?: {
    domain?: string;
    category?: string;
    subcategory?: string;
    issue?: string;
  };
  onIssueClick?: (issue: Issue) => void;
}

const useStyles = makeStyles({
  container: {
    height: '100%',
    overflowY: 'auto',
    padding: tokens.spacingVerticalM,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  issueCard: {
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s, background-color 0.15s, border-color 0.15s',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: tokens.shadow8,
      backgroundColor: tokens.colorNeutralBackground2,
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
  },
  issueContent: {
    padding: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalS,
  },
  pathCode: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase400,
    fontWeight: 'bold',
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: 'semibold',
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.4',
    flex: 1,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  metadataRow: {
    display: 'flex',
    gap: tokens.spacingVerticalS,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: tokens.spacingVerticalXS,
  },
  priorityBadge: {
    fontSize: tokens.fontSizeBase300,
    padding: '4px 8px',
  },
  teamText: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
  },
  emptyState: {
    textAlign: 'center',
    padding: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
  },
});

const IssueList: React.FC<IssueListProps> = ({ issues, currentPath, onIssueClick }) => {
  const styles = useStyles();
  const navigate = useNavigate();

  // Filter issues based on current path
  const filteredIssues = React.useMemo(() => {
    if (!currentPath) {
      // If no path selected, show all issues
      return issues;
    }

    return issues.filter((issue) => {
      if (currentPath.domain && issue.Taxonomy.Domain !== decodeURIComponent(currentPath.domain)) {
        return false;
      }
      if (currentPath.category && issue.Taxonomy.Category !== decodeURIComponent(currentPath.category)) {
        return false;
      }
      if (currentPath.subcategory && issue.Taxonomy.Subcategory !== decodeURIComponent(currentPath.subcategory)) {
        return false;
      }
      if (currentPath.issue && issue.Taxonomy.Issue !== decodeURIComponent(currentPath.issue)) {
        return false;
      }
      return true;
    });
  }, [issues, currentPath]);

  const handleIssueClick = (issue: Issue): void => {
    if (onIssueClick) {
      onIssueClick(issue);
    } else {
      // Default navigation: navigate to the full path
      const path = `/${encodeURIComponent(issue.Taxonomy.Domain)}/${encodeURIComponent(issue.Taxonomy.Category)}/${encodeURIComponent(issue.Taxonomy.Subcategory)}/${encodeURIComponent(issue.Taxonomy.Issue)}/${encodeURIComponent(issue.Path)}`;
      navigate(path);
    }
  };

  if (filteredIssues.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Text>No issues found</Text>
      </div>
    );
  }

  const getPriorityColor = (priority: string): string => {
    const colors: Record<string, string> = {
      Critical: '#d13438',
      High: '#ca5010',
      Medium: '#f2c811',
      Low: '#107c10',
    };
    return colors[priority] || tokens.colorNeutralForeground3;
  };

  return (
    <div className={styles.container}>
      <div className={styles.list}>
        {filteredIssues.map((issue, index) => (
          <div
            key={index}
            className={styles.issueCard}
            onClick={() => handleIssueClick(issue)}
          >
            <div className={styles.issueContent}>
              <div className={styles.headerRow}>
                <Text className={styles.pathCode}>{issue.Path}</Text>
                <Text className={styles.title}>
                  {issue.Taxonomy.RootCause || issue.Taxonomy.Issue}
                </Text>
              </div>
              
              <div className={styles.metadataRow}>
                <Badge
                  appearance="filled"
                  className={styles.priorityBadge}
                  style={{
                    backgroundColor: getPriorityColor(issue.Priority),
                    color: 'white',
                  }}
                >
                  {issue.Priority}
                </Badge>
                <Text className={styles.teamText}>{issue.ResponsibleTeam}</Text>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IssueList;
